import { api } from './api';

const makeRoleHeaders = (userRoleUuid) => {
    if (!userRoleUuid) {
        throw new Error('Missing active user role UUID.');
    }

    return {
        'USER-ROLE': userRoleUuid
    };
};

const buildQueryHeaders = ({
    userRoleUuid,
    caregiverRoleUuid,
    clinicianRoleUuid,
    assignedOnly,
    uploadedOnly,
    hiddenOnly,
    paginationToken,
    paginationLimit
}) => ({
    ...makeRoleHeaders(userRoleUuid),
    ...(caregiverRoleUuid ? { 'CVP-QUERY-CAREGIVER': caregiverRoleUuid } : {}),
    ...(clinicianRoleUuid ? { 'CVP-QUERY-CLINICIAN': clinicianRoleUuid } : {}),
    ...(assignedOnly ? { 'CVP-QUERY-DOC-ASSIGNED': 'true' } : {}),
    ...(uploadedOnly ? { 'CVP-QUERY-DOC-UPLOADED': 'true' } : {}),
    ...(hiddenOnly ? { 'CVP-QUERY-DOC-HIDDEN': 'true' } : {}),
    ...(paginationToken ? { 'PAGINATION-TOKEN': paginationToken } : {}),
    ...(paginationLimit ? { 'PAGINATION-LIMIT': String(paginationLimit) } : {})
});

export const listDocuments = async ({
    userRoleUuid,
    caregiverRoleUuid,
    clinicianRoleUuid,
    assignedOnly,
    uploadedOnly,
    hiddenOnly,
    paginationToken,
    paginationLimit
} = {}) =>
    api.get('/documents', {
        headers: buildQueryHeaders({
            userRoleUuid,
            caregiverRoleUuid,
            clinicianRoleUuid,
            assignedOnly,
            uploadedOnly,
            hiddenOnly,
            paginationToken,
            paginationLimit
        })
    });

export const fetchDocumentByUuid = async ({ userRoleUuid, documentUuid }) =>
    api.post(
        '/documents/fetch',
        { document: documentUuid },
        {
            headers: makeRoleHeaders(userRoleUuid)
        }
    );

export const filterDocumentsByTags = async ({
    userRoleUuid,
    tags,
    caregiverRoleUuid,
    clinicianRoleUuid,
    assignedOnly,
    uploadedOnly,
    hiddenOnly,
    paginationToken,
    paginationLimit
} = {}) =>
    api.post(
        '/documents/filter',
        { tags: Array.isArray(tags) ? tags : [] },
        {
            headers: buildQueryHeaders({
                userRoleUuid,
                caregiverRoleUuid,
                clinicianRoleUuid,
                assignedOnly,
                uploadedOnly,
                hiddenOnly,
                paginationToken,
                paginationLimit
            })
        }
    );

export const uploadDocument = async ({
    userRoleUuid,
    file,
    tags,
    hidden
}) => {
    const formData = new FormData();
    formData.append('file', file);
    const tagsArray = (Array.isArray(tags) ? tags : []).filter(Boolean);
    formData.append(
        'tags',
        new Blob([JSON.stringify(tagsArray)], { type: 'application/json' })
    );
    if (typeof hidden !== 'undefined') {
        formData.append('hidden', String(Boolean(hidden)));
    }

    return api.request({
        url: '/documents/upload',
        method: 'post',
        data: formData,
        headers: {
            ...makeRoleHeaders(userRoleUuid),
            'Content-Type': 'multipart/form-data'
        }
    });
};

export const updateDocument = async ({
    userRoleUuid,
    documentUuid,
    tags,
    name
}) =>
    api.patch(
        '/documents',
        {
            document: documentUuid,
            ...(typeof tags !== 'undefined' ? { tags: Array.isArray(tags) ? tags : [] } : {}),
            ...(typeof name !== 'undefined' ? { name } : {})
        },
        {
            headers: makeRoleHeaders(userRoleUuid)
        }
    );

export const updateDocumentTags = async ({ userRoleUuid, documentUuid, tags }) =>
    updateDocument({
        userRoleUuid,
        documentUuid,
        tags
    });

export const deleteDocument = async ({ userRoleUuid, documentUuid }) =>
    api.delete('/documents', {
        headers: makeRoleHeaders(userRoleUuid),
        data: {
            document: documentUuid
        }
    });

export const assignDocument = async ({ userRoleUuid, documentUuid, caregiverRoleUuid }) =>
    api.post(
        '/documents/assign',
        {
            document: documentUuid,
            caregiver: caregiverRoleUuid
        },
        {
            headers: makeRoleHeaders(userRoleUuid)
        }
    );

export const unassignDocument = async ({ userRoleUuid, documentUuid, caregiverRoleUuid }) =>
    api.delete('/documents/assign', {
        headers: makeRoleHeaders(userRoleUuid),
        data: {
            document: documentUuid,
            caregiver: caregiverRoleUuid
        }
    });

export const downloadDocument = async ({ userRoleUuid, documentUuid }) =>
    api.get(`/documents/download/${documentUuid}`, {
        headers: makeRoleHeaders(userRoleUuid),
        responseType: 'blob'
    });

export const listDocumentCaregivers = async ({
    userRoleUuid,
    documentUuid,
    paginationToken,
    paginationLimit
} = {}) =>
    api.post(
        '/documents/caregivers',
        {
            document: documentUuid
        },
        {
            headers: {
                ...makeRoleHeaders(userRoleUuid),
                ...(paginationToken ? { 'PAGINATION-TOKEN': paginationToken } : {}),
                ...(paginationLimit ? { 'PAGINATION-LIMIT': String(paginationLimit) } : {})
            }
        }
    );
